import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { SpinnerService } from '../spinner/spinner.service';
import { HttpHeaderConstants } from '../http-header.constant';
import { NetsuiteActionSearch } from '../../models/appcloud/netsuite/netsuite-action-search.model';
import { NetsuiteAction } from '../../models/appcloud/netsuite/netsuite-action.model';

@Injectable({
    providedIn: 'root',
})
export class NetSuiteActionService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private NETSUITE_ACTIONS_URL: string = environment.publicApiHost + '/appcloud/netsuite-actions';

    public findAll(
        netsuiteActionSearch: NetsuiteActionSearch,
        additionalHeaders: any = {}
    ): Observable<NetsuiteAction[]> {
        this.#spinnerService.addProcess(
            'NetsuiteActionService.findAll.' + netsuiteActionSearch.eloquainstallId,
            'Loading your actions...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams({ fromObject: <any>netsuiteActionSearch }),
        };

        return this.#http.get<NetsuiteAction[]>(this.NETSUITE_ACTIONS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving NetSuite Action data from Relationship One. Please try again later.'
                );
                return of<NetsuiteAction[]>();
            }),
            finalize(() =>
                this.#spinnerService.completeProcess(
                    'NetsuiteActionService.findAll.' + netsuiteActionSearch.eloquainstallId
                )
            )
        );
    }

    public create(netsuiteAction: NetsuiteAction, additionalHeaders: any = {}): Observable<NetsuiteAction> {
        this.#spinnerService.addProcess('NetsuiteActionService.create', 'Creating your NetSuite action...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        return this.#http.post<NetsuiteAction>(this.NETSUITE_ACTIONS_URL, netsuiteAction, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while creating your NetSuite action. Please try again later.'
                );
                return of<NetsuiteAction>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteActionService.create'))
        );
    }

    public update(id: number, netsuiteAction: NetsuiteAction, additionalHeaders: any = {}): Observable<NetsuiteAction> {
        this.#spinnerService.addProcess('NetsuiteActionService.update', 'Updating your NetSuite action...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.mergePatch, ...additionalHeaders }),
        };
        return this.#http.patch<NetsuiteAction>(this.NETSUITE_ACTIONS_URL + '/' + id, netsuiteAction, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while updating your NetSuite action. Please try again later.'
                );
                return of<NetsuiteAction>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteActionService.update'))
        );
    }

    public delete(id: number, netsuiteAction: NetsuiteAction, additionalHeaders: any = {}): Observable<void> {
        this.#spinnerService.addProcess('NetsuiteActionService.delete', 'Deleting your NetSuite action...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.mergePatch, ...additionalHeaders }),
            body: netsuiteAction,
        };
        return this.#http.delete<void>(this.NETSUITE_ACTIONS_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting your NetSuite action. Please try again later.'
                );
                return of<void>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteActionService.delete'))
        );
    }
}
